import Close from "@mui/icons-material/Close";
import ImageSearch from "@mui/icons-material/ImageSearch";
import Replay from "@mui/icons-material/Replay";
import { Box, Button, Divider } from "@mui/material";
import { useEffect, useState } from "react";

import type { FC, JSX } from "react";

export const UpdateImageryButton: FC<{
  onOpen: () => void;
  onClose: () => void;
  onReset?: () => void;
  disabled?: boolean;
  isOpen?: boolean;
  showReset?: boolean;
}> = ({ onOpen, onClose, onReset, disabled = false, isOpen = false, showReset = false }) => {
  const buttonTop = useAnchoredFloatingTop("imagery-button-anchor");

  return (
    <>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          background: theme.palette.background.default,
          position: "fixed",
          top: buttonTop,
          right: isOpen ? 436 : theme.spacing(3),
          zIndex: theme.zIndex.tooltip + 1,
          borderRadius: 999,
          boxShadow: 3,
          transition: "right 0.3s ease",
          overflow: "hidden",
          pointerEvents: disabled ? "none" : "auto",
          opacity: disabled ? 0.5 : 1,
        })}
      >
        {isOpen && (
          <Button
            data-analytics-id="create-content-previews-close-imagery"
            onClick={onClose}
            startIcon={<Close />}
            sx={{
              color: "text.secondary",
              fontWeight: 400,
              borderRadius: 0,
              px: 2,
            }}
          >
            Close
          </Button>
        )}
        {!isOpen && (
          <Button
            data-analytics-id="create-content-previews-update-imagery"
            onClick={onOpen}
            startIcon={<GradientImageSearch />}
            sx={{
              color: "text.secondary",
              fontWeight: 400,
              borderRadius: 0,
              px: 2,
            }}
          >
            Update imagery
          </Button>
        )}
        {showReset && (
          <>
            <Divider orientation="vertical" flexItem />
            <Button
              data-analytics-id="create-content-previews-reset-imagery"
              onClick={onReset}
              startIcon={<Replay />}
              sx={{
                color: "text.secondary",
                fontWeight: 400,
                borderRadius: 0,
                px: 2,
              }}
            >
              Reset
            </Button>
          </>
        )}
      </Box>
      <div id="imagery-button-anchor" />
    </>
  );
};

const GradientImageSearch = (): JSX.Element => (
  <>
    <svg width={0} height={0}>
      <linearGradient id="linearColors" x1={1} y1={0} x2={1} y2={1}>
        <stop offset={0} stopColor="#00AEEF" />
        <stop offset={1} stopColor="#A033FF" />
      </linearGradient>
    </svg>
    <ImageSearch sx={{ fill: "url(#linearColors)" }} />
  </>
);

function useAnchoredFloatingTop(anchorId: string): number {
  const minimumTop = 16;
  const intervalMs = 150;
  const [top, setTop] = useState<number>(minimumTop);

  useEffect(() => {
    const anchor = document.getElementById(anchorId);
    if (!anchor) return undefined;

    const updateTop = (): void => {
      const rect = anchor.getBoundingClientRect();
      const nextTop = Math.max(minimumTop, rect.top);
      setTop((prev) => (prev === nextTop ? prev : nextTop));
    };

    updateTop();

    const interval = globalThis.setInterval(updateTop, intervalMs);

    const onScroll = (): void => updateTop();
    document.addEventListener("scroll", onScroll, true);

    const onResize = (): void => updateTop();
    window.addEventListener("resize", onResize);

    return () => {
      clearInterval(interval);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [anchorId, intervalMs]);

  return top;
}
